import { BodyText, Loader } from '@unmind/design-system';
import { CheckNoBorder, ChevronBold, Hearts, Target, Tools } from 'icons';
import { rem } from 'polished';
import styled, { css } from 'styled-components';

export const Container = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  min-width: ${rem(120)};
`;

export const InputContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.background.secondary};
    border: 1px solid ${theme.colors.border.secondary};
  `}

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${rem(4)} ${rem(6)};
  border-radius: 6px;
  gap: ${rem(4)};
  cursor: pointer;
`;

export const InputText = styled(BodyText).attrs(({ theme }) => ({
  color: theme.colors.text.primary,
  sizes: [theme.typography.fontSizes.fontSize14],
  weight: theme.typography.fontWeights.semiBold,
}))``;

interface ToggleIconProps {
  $isOpen?: boolean;
}

export const ToggleIcon = styled(ChevronBold).attrs(({ theme }) => ({
  width: 12,
  height: 12,
  primaryColor: theme.colors.text.primary,
}))<ToggleIconProps>`
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      transform: scaleY(-1);
    `};
`;

interface MenuProps {
  $isOpen?: boolean;
  $onHomeScreen?: boolean;
}

export const Menu = styled.div<MenuProps>`
  ${({ theme, $isOpen }) => css`
    display: ${$isOpen ? 'flex' : 'none'};
    background-color: ${theme.colors.background.card};
    box-shadow: 0 2px 8px 0 ${theme.colors.boxShadow};
  `}

  ${({ $onHomeScreen }) =>
    $onHomeScreen
      ? css`
          top: 100%;
          margin-top: 4px;
        `
      : css`
          bottom: 100%;
          margin-bottom: 4px;
        `}

  position: absolute;
  left: 0;
  min-width: 100%;
  width: clamp(100%, 42ch, min(356px, 45vw));
  flex-direction: column;
  border-radius: 8px;
  z-index: 20;
  overflow: hidden;
`;

export const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: ${rem(8)} ${rem(16)};
  cursor: pointer;
  gap: ${rem(8)};
  width: 100%;

  ${({ theme }) => css`
    &:hover {
      background: ${theme.colors.background.cardHover};
    }
  `}
`;

export const ItemText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

export const ItemLabel = styled(BodyText).attrs(({ theme }) => ({
  color: theme.colors.text.primary,
  sizes: [theme.typography.fontSizes.fontSize14],
  weight: theme.typography.fontWeights.semiBold,
}))`
  text-wrap: wrap;
`;

export const ItemDescription = styled(BodyText).attrs(({ theme }) => ({
  color: theme.colors.text.secondary,
  sizes: [theme.typography.fontSizes.fontSize12],
  weight: theme.typography.fontWeights.regular,
}))`
  text-wrap: wrap;
`;

export const SelectedIcon = styled(CheckNoBorder).attrs(({ theme }) => ({
  width: 16,
  height: 16,
  primaryColor: theme.colors.text.primary,
}))`
  margin-left: ${rem(16)};
  flex-shrink: 0;
`;

export const ActiveListeningIcon = styled(Hearts).attrs(({ theme }) => ({
  width: 16,
  height: 16,
  primaryColor: theme.colors.text.primary,
}))`
  flex-shrink: 0;
`;

export const CoachingIcon = styled(Target).attrs(({ theme }) => ({
  width: 16,
  height: 16,
  primaryColor: theme.colors.text.primary,
}))`
  flex-shrink: 0;
`;

export const PracticalToolsIcon = styled(Tools).attrs(({ theme }) => ({
  width: 16,
  height: 16,
  primaryColor: theme.colors.text.primary,
}))`
  flex-shrink: 0;
`;

export const LoadingContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.background.secondary};
    border: 1px solid ${theme.colors.border.secondary};
  `}

  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(4)} ${rem(6)};
  border-radius: 6px;
`;

export const LoadingIndicator = styled(Loader).attrs(({ theme }) => ({
  size: 4,
  color: theme.colors.text.primary,
}))``;
